/**
 * MilestoneEntry (hybrid) — embeds a real project milestone + its merged documents.
 * The entry references milestone_airtable_id; the API resolves entry.milestone +
 * entry.documents (legacy Airtable docs + Supabase uploads).
 * data: { intro?: string }  (optional lead-in text shown above the files)
 * Read-only: the client opens documents in a new tab.
 *
 * Rendering paths:
 *   Pre-design Research              → PreDesignEntry (portrait flip viewer)
 *   Sheet-gate milestones            → DrawingSheetEntry (DrawingSetViewer + 3D booklet)
 *   Selections milestones            → SelectionsEntry (Outline Spec + Schedules books)
 *   Specifications milestones        → SpecificationEntry (portrait flip viewer)
 *   Rendering Package                → RenderingPackageEntry (masonry gallery)
 *   All other milestone types        → default document list
 *
 * Visual language (the luxury pass): centered editorial composition — tracked uppercase
 * eyebrow + light uppercase title over a short centered hairline (EntryHead), documents as
 * quiet hairline list rows (DocRow) rather than boxed badge cards, and tracked uppercase
 * text links instead of blue web links.
 */

import { useState, lazy, Suspense } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, PrimaryButton, GhostButton, DoneChip } from '../journeyUI.jsx'
import PresentationLink from './PresentationLink.jsx'
import PdfFlipViewer from '../../components/PdfFlipViewer.jsx'
import JourneyBook from './JourneyBook.jsx'
import SelectionsReview from './SelectionsReview.jsx'
import { isSheetGateMilestone } from '../eligibility.js'
import RenderingPackageEntry from './RenderingPackageEntry.jsx'
import RoomProgramSection from './RoomProgramSection.jsx'
import { getCachedPinProof, getOptionalStaffToken } from '../../services/journey.js'
import { PRE_DESIGN_RESEARCH_TYPE_ID, RENDERING_PACKAGE_TYPE_ID, selectionsDocKinds, SELECTIONS_DOC_LABELS } from '../../outline/constants.js'

const DrawingSetViewer = lazy(() => import('../../components/DrawingSetViewer.jsx'))

/* Centered entry head — eyebrow, uppercase light title, short centered hairline. */
function EntryHead({ eyebrow, title }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 10 }}>
      <div style={{ ...J.eyebrow, marginBottom: 18 }}>{eyebrow}</div>
      <h2 style={{ ...J.title, maxWidth: 440, margin: '0 auto' }}>{title}</h2>
      <div style={{ width: 36, height: 1, background: 'rgba(7,39,45,0.24)', margin: '26px auto 30px' }} />
    </div>
  )
}

/* Centered muted placeholder for not-yet-issued content. */
function EntryPlaceholder({ children }) {
  return (
    <div style={{ ...J.body, fontStyle: 'italic', color: '#8ba6b1', textAlign: 'center', padding: '18px 0 8px' }}>
      {children}
    </div>
  )
}

/* Supporting document as a quiet hairline list row: tracked type label · name · OPEN. */
function DocRow({ doc, first }) {
  const [hover, setHover] = useState(false)
  return (
    <a href={doc.url} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 16, textDecoration: 'none',
        padding: '15px 4px', borderTop: first ? 'none' : '0.5px solid rgba(7,39,45,0.08)',
        background: hover ? 'rgba(7,39,45,0.025)' : 'transparent', transition: 'background 0.15s',
      }}>
      <span style={{
        fontFamily: '"Montserrat", sans-serif', fontSize: 8, fontWeight: 500,
        letterSpacing: '0.24em', textTransform: 'uppercase', color: '#9db3bd', flexShrink: 0, width: 42,
      }}>
        {(doc.file_type || 'link').slice(0, 4)}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#17323a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {doc.name}
      </span>
      <span style={{
        fontFamily: '"Montserrat", sans-serif', fontSize: 8, fontWeight: 500,
        letterSpacing: '0.24em', textTransform: 'uppercase',
        color: hover ? '#07272D' : '#8ba6b1', flexShrink: 0, transition: 'color 0.15s',
      }}>
        Open
      </span>
    </a>
  )
}

function DocList({ docs }) {
  return (
    <div style={{ marginTop: 6 }}>
      {docs.map((d, i) => <DocRow key={d.id} doc={d} first={i === 0} />)}
    </div>
  )
}

/* Pre-Design Research milestone: the frozen report (a 'predesign-report' document) opens in an
   interactive portrait flip viewer with a download link, instead of the plain file list. */
function PreDesignEntry({ entry, ms, docs, reportDoc, opcDoc, token }) {
  const [open, setOpen] = useState(null)   // 'report' | 'opc' | 'site' | null
  // Site Analysis is a third book alongside Report/Criteria — same group, not a plain file below it.
  const siteDoc = docs.find(d => d.source === 'site-analysis')
  const others = docs.filter(d => d !== reportDoc && d !== opcDoc && d !== siteDoc)
  const reportTitle = entry.title || ms?.name || 'Pre-Design Research Report'
  const hasBooks = !!(reportDoc || opcDoc || siteDoc)
  return (
    <div>
      <EntryHead eyebrow={ms?.phase || 'Pre-Design Research'} title={entry.title || ms?.name || 'Pre-Design Research'} />
      {entry.data?.intro && <div style={{ ...J.body, marginBottom: 26, textAlign: 'center', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}><Markdown text={entry.data.intro} /></div>}
      {hasBooks ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap', padding: '10px 0 6px', marginBottom: others.length ? 34 : 0 }}>
            {reportDoc && <JourneyBook label="Report" aspect={8.5 / 11} pageCount={reportDoc.page_count || null} onOpen={() => setOpen('report')} downloadUrl={reportDoc.url} />}
            {opcDoc && <JourneyBook label="Criteria" aspect={8.5 / 11} pageCount={opcDoc.page_count || null} onOpen={() => setOpen('opc')} downloadUrl={opcDoc.url} />}
            {siteDoc && <JourneyBook label="Site Analysis" pageCount={siteDoc.page_count || null} onOpen={() => setOpen('site')} downloadUrl={siteDoc.url} />}
          </div>
          {others.length > 0 && <DocList docs={others} />}
          {open === 'report' && reportDoc && <PdfFlipViewer pdfUrl={reportDoc.url} title={reportTitle} onClose={() => setOpen(null)} />}
          {open === 'opc' && opcDoc && <PdfFlipViewer pdfUrl={opcDoc.url} title="Owner's Project Criteria" onClose={() => setOpen(null)} />}
          {open === 'site' && siteDoc && <PdfFlipViewer pdfUrl={siteDoc.url} title="Site Analysis" landscape onClose={() => setOpen(null)} />}
        </>
      ) : (
        <EntryPlaceholder>The pre-design research report will appear here once this milestone is issued.</EntryPlaceholder>
      )}
      {token && <RoomProgramSection roomProgram={entry.roomProgram} token={token} entryId={entry.id} />}
    </div>
  )
}

const SPEC_MS_TYPE_IDS = new Set([
  'recJv58DnUxXKEfo0', // Short Form Residential Specifications
  'recHXZnhdTpAg8ZUF', // Three Part Technical Specifications
])

/* Specifications milestone: portrait report viewer that opens the spec PDF. */
function SpecificationEntry({ entry, ms, docs }) {
  const [open, setOpen] = useState(false)
  // Detect PDF by file_type OR URL extension; fall back to first doc so the booklet always shows.
  const isPdf = d => /pdf/i.test(d.file_type || '') || /\.pdf($|\?)/i.test(d.url || '')
  const specDoc = docs.find(isPdf) || docs[0] || null
  const canFlip = !!(specDoc && isPdf(specDoc))
  const others = docs.filter(d => d !== specDoc)
  function openSpec() {
    if (canFlip) setOpen(true)
    else if (specDoc) window.open(specDoc.url, '_blank', 'noopener,noreferrer')
  }
  return (
    <div>
      <EntryHead eyebrow={ms?.phase || 'Specifications'} title={entry.title || ms?.name || 'Specifications'} />
      {entry.data?.intro && <div style={{ ...J.body, marginBottom: 26, textAlign: 'center', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}><Markdown text={entry.data.intro} /></div>}
      {specDoc ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px', marginBottom: others.length ? 34 : 0 }}>
            <JourneyBook label="Specifications" aspect={8.5 / 11} pageCount={specDoc.page_count || null} onOpen={openSpec} downloadUrl={specDoc.url} />
          </div>
          {others.length > 0 && <DocList docs={others} />}
          {open && canFlip && <PdfFlipViewer pdfUrl={specDoc.url} title={entry.title || ms?.name || 'Specifications'} onClose={() => setOpen(false)} />}
        </>
      ) : (
        <EntryPlaceholder>The specifications will appear here once issued.</EntryPlaceholder>
      )}
    </div>
  )
}

/* Selections milestones (docs/SELECTIONS_PACKAGE_DOCUMENTS.md): one book per issued Documents row —
   Outline Specifications (portrait) and Schedules (landscape) on Architectural Selections, Schedules
   only on Interior Selections. The rows are re-printed from what was issued when the milestone posts;
   the server already withholds the pre-post review copies (api/_shareDocs.js), and this only shows
   books once the milestone is posted as a second guard. */
/* THE ANSWER, ON THE SAME CARD (Matt, 2026-10-26: "not two cards, one for seeing them and one for
   approving them"). Once the package is issued the client approves it or asks for changes here.
   api/journey-respond.js posts the client review and turns a change request into a thread. */
function SelectionsAnswer({ response, onRespond, label }) {
  const [mode, setMode] = useState(null)   // null | 'changes'
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const decided = response && (response.kind === 'approve' || response.kind === 'request_changes')
  const wrap = { borderTop: '0.5px solid rgba(7,39,45,0.12)', marginTop: 30, paddingTop: 22, textAlign: 'center' }
  if (decided) {
    const approved = response.kind === 'approve'
    const when = response.created_at ? new Date(response.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : ''
    return (
      <div style={wrap}>
        <DoneChip>{approved ? 'Approved' : 'Changes requested'}{when ? ' · ' + when : ''}</DoneChip>
        {response.payload?.note && <div style={{ ...J.body, marginTop: 14, fontStyle: 'italic' }}>&ldquo;{response.payload.note}&rdquo;</div>}
        <div style={{ ...J.body, marginTop: 12, fontSize: 13, color: '#7a9aaa' }}>
          {approved ? 'Thank you. We will carry these selections into the construction documents.' : 'Thank you. We will make these changes and show you the update.'}
        </div>
      </div>
    )
  }
  async function send(kind, payload) {
    if (busy) return
    setBusy(true)
    try { await onRespond(kind, payload) } finally { setBusy(false) }
  }
  return (
    <div style={wrap}>
      <div style={{ ...J.body, marginBottom: 16 }}>Once you have looked through the {label}, let us know.</div>
      {mode === 'changes' ? (
        <div style={{ textAlign: 'left', maxWidth: 520, margin: '0 auto' }}>
          <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus
            placeholder="What would you like changed?"
            style={{ width: '100%', minHeight: 100, padding: 12, borderRadius: 10, resize: 'vertical',
              border: '0.5px solid rgba(7,39,45,0.25)', fontFamily: '"DM Sans", sans-serif',
              fontSize: 14, color: '#07272D', outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'center' }}>
            <PrimaryButton disabled={!note.trim() || busy} onClick={() => send('request_changes', { note: note.trim() })}>Send my changes</PrimaryButton>
            <GhostButton disabled={busy} onClick={() => { setMode(null); setNote('') }}>Cancel</GhostButton>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <PrimaryButton disabled={busy} onClick={() => send('approve')}>Approve the selections</PrimaryButton>
          <GhostButton disabled={busy} onClick={() => setMode('changes')}>Request changes</GhostButton>
        </div>
      )}
    </div>
  )
}

function SelectionsEntry({ entry, ms, docs, kinds, response, onRespond, token }) {
  const [open, setOpen] = useState(null)   // kind | null
  const posted = !!ms?.postedDate
  const byKind = {}
  if (posted) for (const d of docs) if (kinds.includes(d.source)) byKind[d.source] = d
  const books = kinds.filter(k => byKind[k])
  const others = docs.filter(d => !kinds.includes(d.source))
  const openDoc = open && byKind[open]
  const lineByLine = posted && !!onRespond && !!entry.schedule?.items?.length
  const label = (entry.title || ms?.name || 'selections').replace(/ & Outline Spec$/i, '')
  // With schedule items to answer, the Schedules book opens the line-by-line list, not the flip book.
  const openBook = (k) => setOpen(k === 'schedules' && lineByLine ? 'review' : k)
  return (
    <div>
      <EntryHead eyebrow={ms?.phase || 'Selections'} title={entry.title || ms?.name || 'Selections'} />
      {entry.data?.intro && <div style={{ ...J.body, marginBottom: 26, textAlign: 'center', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}><Markdown text={entry.data.intro} /></div>}
      {books.length ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap', padding: '10px 0 6px', marginBottom: others.length ? 34 : 0 }}>
          {books.map(k => (
            <JourneyBook key={k} label={SELECTIONS_DOC_LABELS[k]} aspect={k === 'schedules' ? 11 / 8.5 : 8.5 / 11}
              pageCount={byKind[k].page_count || null} onOpen={() => openBook(k)} downloadUrl={byKind[k].url} />
          ))}
        </div>
      ) : (
        <EntryPlaceholder>The {kinds.map(k => SELECTIONS_DOC_LABELS[k].toLowerCase()).join(' and ')} will appear here once issued.</EntryPlaceholder>
      )}
      {others.length > 0 && <DocList docs={others} />}
      {posted && books.length > 0 && onRespond && (lineByLine
        ? <SelectionsReview entry={entry} response={response} onRespond={onRespond} token={token} label={label}
            open={open === 'review'} onOpen={() => setOpen('review')} onClose={() => setOpen(null)} pdfUrl={byKind.schedules && byKind.schedules.url} />
        : <SelectionsAnswer response={response} onRespond={onRespond} label={label} />)}
      {openDoc && <PdfFlipViewer pdfUrl={openDoc.url} title={SELECTIONS_DOC_LABELS[open]} landscape={open === 'schedules'} onClose={() => setOpen(null)} />}
    </div>
  )
}

/* Drawing-sheet milestone: booklet viewer + a compile-on-click download. The interactive sheet
   viewer (built live off the current sheet revisions) is the sole authoritative drawing content
   here — we never host a rolled-up "full set" PDF as a milestone document (version-control risk:
   it can drift from the live sheets). The download link instead calls api/journey-drawings-pdf,
   which re-resolves the SAME current sheet set server-side and merges it into a PDF on demand —
   nothing is stored, so it can never go stale; it just looks like a file to the client. */
function DrawingSheetEntry({ entry, ms, token }) {
  const [viewer, setViewer] = useState(false)
  const [compiling, setCompiling] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [linkHover, setLinkHover] = useState(false)
  const sheets = entry.sheets || []
  const title = entry.title || ms?.name || 'Drawing Set'

  async function handleDownload() {
    if (compiling || !ms?.id || !token) return
    setCompiling(true); setDownloadError('')
    try {
      const staffToken = await getOptionalStaffToken(token)
      const res = await fetch('/api/journey-drawings-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(staffToken ? { Authorization: 'Bearer ' + staffToken } : {}),
        },
        body: JSON.stringify({ token, milestoneId: ms.id, filename: title + '.pdf', pinProof: getCachedPinProof(token) }),
      })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Download failed') }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = title + '.pdf'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } catch (err) {
      setDownloadError(err.message || 'Could not compile PDF')
    } finally {
      setCompiling(false)
    }
  }

  return (
    <div>
      <EntryHead eyebrow={ms?.phase || 'Drawing Set'} title={title} />
      {entry.data?.intro && <div style={{ ...J.body, marginBottom: 26, textAlign: 'center', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}><Markdown text={entry.data.intro} /></div>}
      {sheets.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0 6px' }}>
          <JourneyBook label="Drawings" pageCount={sheets.length} onOpen={() => setViewer(true)} />
          <button
            onClick={handleDownload} disabled={compiling}
            onMouseEnter={() => setLinkHover(true)}
            onMouseLeave={() => setLinkHover(false)}
            style={{
              ...J.textLink,
              marginTop: 18,
              color: linkHover && !compiling ? '#07272D' : '#6b8f95',
              borderBottomColor: linkHover && !compiling ? 'rgba(7,39,45,0.5)' : 'rgba(7,39,45,0.22)',
              cursor: compiling ? 'default' : 'pointer',
              opacity: compiling ? 0.6 : 1,
            }}
          >
            {compiling ? 'Compiling…' : 'Download PDF'}
          </button>
          {downloadError && <div style={{ ...J.mono, color: '#c0392b', marginTop: 8 }}>{downloadError}</div>}
        </div>
      ) : (
        <EntryPlaceholder>The drawing set will appear here once issued.</EntryPlaceholder>
      )}
      {viewer && (
        <Suspense fallback={null}>
          <DrawingSetViewer drawings={sheets} onClose={() => setViewer(false)} />
        </Suspense>
      )}
    </div>
  )
}

export default function MilestoneEntry({ entry, token, previewMode, response, onRespond, viewOnly }) {
  const ms = entry.milestone
  const pres = entry.presentation || null      // set when the milestone is a presentation type
  // Prefer the stored entry type id; fall back to the Airtable milestone's own live type id
  // (handles entries created before milestone_type_id was being stored in the DB — both are
  // frozen SNAPSHOTS by design, see journey/eligibility.js's note, so an already-published entry
  // never changes classification under a later deliverable-type rename). Classification always
  // goes through msTypeId now (2026-09-22 — was matched by name, which breaks silently on a
  // rename); the NAME (entry.milestone_type / ms?.type) is no longer read anywhere in this file.
  const msTypeId = entry.milestone_type_id || ms?.typeId || ''

  // freezeSiteAnalysis runs on EVERY milestone post (the analysis is project-scoped, not milestone-
  // scoped — see docs/SITE_ANALYSIS.md), so a stray 'site-analysis' document can land on whichever
  // milestone happened to post most recently. On the Client Hub it only ever belongs on
  // Pre-design Research, where the diagram is actually part of that deliverable's story.
  const docs = (entry.documents || []).filter(d => msTypeId === PRE_DESIGN_RESEARCH_TYPE_ID || d.source !== 'site-analysis')

  // Pre-Design Research milestones get the interactive report viewer. Detect by the frozen report
  // document (source 'predesign-report', or the name we stamp) or the milestone type.
  const reportDoc = docs.find(d => d.source === 'predesign-report' || /pre-?design research report/i.test(d.name || ''))
  const opcDoc = docs.find(d => d.source === 'opc-report' || /owner.?s project criteria/i.test(d.name || ''))
  if (reportDoc || opcDoc || msTypeId === PRE_DESIGN_RESEARCH_TYPE_ID) {
    return <PreDesignEntry entry={entry} ms={ms} docs={docs} reportDoc={reportDoc} opcDoc={opcDoc} token={token} />
  }

  // Sheet-gate milestones render as an interactive drawing set viewer.
  if (isSheetGateMilestone(msTypeId)) {
    return <DrawingSheetEntry entry={entry} ms={ms} token={token} />
  }

  // Selections milestones render one book per issued Outline Spec / Schedules row.
  const selKinds = selectionsDocKinds(msTypeId)
  if (selKinds.length) {
    // An owner's rep reads the selections; the owner answers them (docs/CLIENT_HUB_OWNERS_REP.md).
    return <SelectionsEntry entry={entry} ms={ms} docs={docs} kinds={selKinds} response={response} onRespond={viewOnly ? null : onRespond} token={token} />
  }

  // Specifications milestones render as a portrait report viewer.
  if (SPEC_MS_TYPE_IDS.has(msTypeId)) {
    return <SpecificationEntry entry={entry} ms={ms} docs={docs} />
  }

  // Rendering Package milestones render as a masonry image gallery.
  if (msTypeId === RENDERING_PACKAGE_TYPE_ID) {
    return <RenderingPackageEntry entry={entry} token={token} />
  }

  return (
    <div>
      <EntryHead eyebrow={ms?.phase || 'Milestone'} title={entry.title || ms?.name || 'Milestone'} />
      {entry.data?.intro && (
        <div style={{ ...J.body, marginBottom: 26, textAlign: 'center', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}><Markdown text={entry.data.intro} /></div>
      )}
      {pres && token ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px', marginBottom: docs.length ? 30 : 0 }}>
          <PresentationLink
            presentation={pres} token={token} previewMode={previewMode}
            renderTrigger={({ onClick }) => <JourneyBook label="Presentation" onOpen={onClick} />}
          />
        </div>
      ) : null}
      {docs.length === 0 ? (
        pres ? null : (
          <EntryPlaceholder>Documents for this milestone will appear here.</EntryPlaceholder>
        )
      ) : (
        <DocList docs={docs} />
      )}
    </div>
  )
}
